import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {DialogActions, DialogContent, DialogTitle, IconButton} from "@mui/material";
import {createUUId} from "../../../../core/services/utils/utils";
import ManagePartSupportVendorStatusesDialog from "../manage-part-support-vendor-statuses";
import Api from "../../../../core/services/api/api";
import {OpenInNew, WarningOutlined} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../hooks/use-access-levels";

// TODO: June 02 2025 - add vendor reason

const schema = Yup.object().shape({
    status: Yup.mixed().nullable().required(ValidateMessages.required),
    reason: Yup.object().nullable(),
})

const required = makeRequired(schema);
const validate = makeValidate(schema);

const UpsertPartSupportVendorStatusDialog = ({open, setOpen: close, onSuccess, statusId, requestId}) => {
    const [submitting, setSubmitting] = useState(false);
    const [manageStatuses, setManageStatuses] = useState(false);
    const [anyStatusesRemoved, setAnyStatusesRemoved] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const formId = useRef(createUUId(true));
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();

    const initialValues = useMemo(() => ({
        status: statuses.find(e => e.id === statusId) ?? null,
    }), [statuses, statusId]);


    /**
     * Sets the open state of the dialog.
     * @type {function(*): *}
     */
    const setOpen = useCallback((value) => close(value, anyStatusesRemoved), [anyStatusesRemoved, close])

    /**
     * Fetches the statuses from the server.
     * @type {(function(): Promise<void>)|*}
     */
    const getStatuses = useCallback(async () => {
        const response = await Api.getPartSupportVendorStatuses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setStatuses(response.data ?? []);
        }
    }, [isMounted])

    /**
     * Fetches the statuses from the server.
     */
    useEffect(() => {
        if (open) {
            getStatuses().then();
            setAnyStatusesRemoved(false);
        }
    }, [open, getStatuses]);

    /**
     * Changes the status of a request, and if the new status is not same as the status of the request, then calls the
     * api to change it in the server too.
     * @param values {any}
     */
    const changeStatus = useCallback(async (values) => {
        if (values.status.id === statusId) {
            setOpen(false);
            return;
        }
        setSubmitting(true);
        const forApi = {
            id: requestId,
            vendorStatusId: values.status.id,
            vendorStatusReasonId: values.reason?.id,
        }
        const response = await Api.updateVendorStatusOfPartSupport(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            setOpen(false);
            onSuccess(requestId, values.status, values.reason);
        }
    }, [isMounted, onSuccess, requestId, setOpen, statusId])

    const statusOptions = useMemo(() => statuses?.map(e => ({
        value: e,
        label: e.title,
        id: e.id,
    })) ?? [], [statuses]);

    return (
        <>
            <ManagePartSupportVendorStatusesDialog
                open={manageStatuses}
                onClose={(newStatuses) => {
                    if (newStatuses) {
                        setStatuses(prevState => {
                            const prevIds = prevState.map(e => e.id);
                            const newIds = newStatuses.map(e => e.id);
                            const anyStatusesRemoved = prevIds.some(e => !newIds.includes(e));
                            setAnyStatusesRemoved(anyStatusesRemoved);
                            if (anyStatusesRemoved) {
                                const currentStatusId = formRef.current.getFieldState('status')?.value?.id;
                                if (currentStatusId && !newIds.includes(currentStatusId)) {
                                    formRef.current.change('status', newStatuses.find(e => e.isDefault) ?? null);
                                } else {
                                    formRef.current.change('status', null);
                                }
                            }
                            return newStatuses;
                        });
                    }
                    setManageStatuses(false);
                }}
            />
            <Dialog
                open={open}
                setOpen={setOpen}
            >
                <DialogTitle sx={{padding: 0}}>
                    <Row xs={12} className='no-gutters justify-content-center p-3 w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            Change Request Vendor Status
                        </p>
                    </Row>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent>
                    <Form
                        className='w-100'
                        onSubmit={changeStatus}
                        validate={validate}
                        id={formId.current}
                        initialValues={initialValues}
                        render={({form, values}) => {
                            formRef.current = form;
                            const reasons = values?.status?.reasons ?? [];
                            return (
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Col xs={12} className={'my-3 w-100'}>
                                        <p className={'text-color-3 text-sm'}>
                                            Please select the new status and the most relevant reason for the change.
                                            Please select the new status for the change.
                                        </p>
                                    </Col>
                                    <Col xs={12} className='px-2 mb-3'>
                                        <OutlinedDropdownForm
                                            fontSize={14}
                                            fullWidth={true}
                                            onChange={(e) => {
                                                form.change(e.target.name, e.target.value);
                                                // form.change('reason', undefined);
                                            }}
                                            label={'Status'}
                                            name={'status'}
                                            required={required.status}
                                            data={statusOptions}
                                        />
                                    </Col>
                                    {
                                        statusOptions.length === 0 &&
                                        operatorAccessLevels.packages.warrantyAndReturn.editConfigurations &&
                                        <Col xs={12} className='px-2 mb-3'>
                                            <p className={'text-sm text-color-3 text-center'}>
                                                <WarningOutlined
                                                    color={'warning'}
                                                    sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                                />
                                                There are no statuses to select from. Please create a status first.
                                                <IconButton
                                                    color={'paper.primary'}
                                                    component={Link}
                                                    to={routes.warrantyAndReturn.settings.statuses.concat('?tab=vendors')}
                                                    target={'_blank'}
                                                    sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                                    onClick={() => setOpen(false)}
                                                >
                                                    <OpenInNew/>
                                                </IconButton>
                                            </p>
                                        </Col>
                                    }
                                    <Col xs={12} className='px-2 mb-3'>
                                        <OutlinedDropdownForm
                                            fontSize={14}
                                            fullWidth={true}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            label={'Reason for Change'}
                                            name={'reason'}
                                            required={!!required.reason}
                                            data={reasons?.map(e => ({
                                                value: e,
                                                label: e.title,
                                                id: e.id,
                                            })) ?? []}
                                        />
                                    </Col>
                                </Row>
                            );
                        }}
                    />
                    <div className={'my-3'}/>
                </DialogContent>
                <DialogActions className={'d-flex flex-column w-100 p-0'}>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-center w-100 m-0'>
                        <Button
                            disabled={!open || submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => setOpen(false)}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {submitting ? 'submitting...' : 'Close'}
                        </Button>
                        <div className={'d-flex justify-content-end'}>
                            <Button
                                disabled={!open || submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={formId.current}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {submitting ? 'submitting...' : 'Change'}
                            </Button>
                        </div>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UpsertPartSupportVendorStatusDialog;
